import { create } from 'zustand';
import { PopupInfo, PopupOptions } from '../types/Popup';

/**
 *
 */
interface PopupStoreState {
  entry: Record<string, PopupInfo>;
  order: string[];
  get: (id: string) => PopupInfo | undefined;
  open: (id: string, options?: PopupOptions) => void;
  close: (id: string) => void;
  clear: (id: string) => void;
  peek: (visible?: boolean) => PopupInfo | undefined;
  add: (id: string) => void;
  reset: () => void;
  size: () => number;
}

/**
 *
 */
const usePopupStore = create<PopupStoreState>((set, get) => ({
  /**
   * 팝업 엔트리 목록
   */
  entry: {},
  /**
   * 팝업 배열
   */
  order: [],

  get: (id: string) => {
    const entry = get().entry;
    return entry[id];
  },

  /**
   * 팝업을 오픈한다.
   * @param id 팝업ID
   */
  open: (id: string, options?: PopupOptions) => {
    set((state) => {
      const existInOrder = state.order.includes(id);

      return {
        order: existInOrder ? state.order : [...state.order, id],
        entry: {
          ...state.entry,
          [id]: {
            id,
            visible: true,
            options,
          },
        },
      };
    });
  },

  /**
   * 팝업을 닫는다.
   * @param id 팝업ID
   */
  close: (id: string) => {
    set((state) => {
      const current = state.entry[id];
      if (!current) {
        return {
          ...state,
        };
      }

      const next = { ...state.entry };
      delete next[id];
      return {
        entry: {
          ...next,
          [id]: {
            id,
            visible: false,
          },
        },
      };
    });
  },

  /**
   * 팝업을 store에서 제거한다.
   * @param id 팝업ID
   */
  clear: (id: string) => {
    set((state) => {
      const next = { ...state.entry };
      delete next[id];
      return {
        entry: next,
        order: state.order.filter((v) => v !== id),
      };
    });
  },

  /**
   * 팝업 store reset
   */
  reset: () => {
    set(() => ({
      entry: {},
      order: [],
    }));
  },

  /**
   * 마지막 팝업정보를 획득한다.
   * @returns
   */
  peek: (visible?: boolean) => {
    const current = get().entry;
    const arr = Object.values(current);
    if (!arr || arr.length === 0) {
      return;
    }

    if (visible) {
      const filter = arr.filter((v) => v.visible);
      return filter[filter.length - 1] as PopupInfo;
    }
    return arr[arr.length - 1] as PopupInfo;
  },

  /**
   * 팝업을 추가한다.
   * @param id 팝업ID
   */
  add: (id: string) => {
    set((state) => {
      const existInOrder = state.order.includes(id);

      return {
        order: existInOrder ? state.order : [...state.order, id],
        entry: {
          ...state.entry,
          [id]: {
            id,
            visible: true,
          },
        },
      };
    });
  },

  /**
   * store에 저장된 팝업 size를 계산하여 획득한다.
   * @returns
   */
  size: () => {
    const entry = get().entry;
    if (!entry) return 0;
    return Object.keys(entry).length;
  },
}));

export { usePopupStore };
